import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';

/**
 * Reads the latest `analytics_snapshots.response_summary` for one
 * child. The summary is the AI development insight described in
 * §08.4 — produced nightly by the analytics Edge Function. RLS on
 * analytics_snapshots scopes parent reads to their linked children.
 *
 * The parent only sees this surface when the parent has granted
 * `AI_DEVELOPMENT_ANALYSIS` consent (gated in AiNote.tsx via
 * `useParentConsents`). RLS doesn't enforce the consent — that's a
 * UI concern + the snapshot job won't run for non-opted-in players
 * anyway (see /lib/auth/aiOptIn).
 */

export interface ChildAiNote {
  responseSummary: string | null;
  snapshotDate: string | null;
}

interface State {
  data: ChildAiNote | null;
  loading: boolean;
  error: string | null;
}

export function useChildAiNote(playerId: string | null): State {
  const [state, setState] = useState<State>({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!playerId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState({ data: null, loading: true, error: null });

    (async () => {
      const { data, error } = await supabase
        .from('analytics_snapshots')
        .select('response_summary, snapshot_date')
        .eq('player_id', playerId)
        .order('snapshot_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setState({ data: null, loading: false, error: error.message });
        return;
      }
      if (!data) {
        setState({
          data: { responseSummary: null, snapshotDate: null },
          loading: false,
          error: null,
        });
        return;
      }
      setState({
        data: {
          responseSummary: ((data as any).response_summary as string | null) ?? null,
          snapshotDate: ((data as any).snapshot_date as string | null) ?? null,
        },
        loading: false,
        error: null,
      });
    })();

    return () => { cancelled = true; };
  }, [playerId]);

  return state;
}
